import {
  CustomerContent,
  CustomerInput,
  CustomerSummary,
  PaginatedCustomers
} from 'types/CustomerTypes';
import useHttp from '../helpers/HttpMethods';

const http = useHttp();

class CustomersService {
  /**
   * Busca os clientes paginados.
   * @param {string} filter - Filtros em string.
   * @returns {Promise<PaginatedCustomers | Response>} - Clientes em formato paginado.
   */
  static async list(filter: string): Promise<PaginatedCustomers | Response> {
    return await http.get(`/customers?${filter}`);
  }

  /**
   * Busca os clientes associados a um contato.
   * @param {number} contactId - ID do contato.
   * @returns {Promise<CustomerSummary[] | Response>} - Lista de clientes associados ao contato.
   */
  static async listByContact(
    contactId: number
  ): Promise<CustomerSummary[] | Response> {
    return await http.get(`/customers/by-contact/${contactId}`);
  }

  /**
   * Busca um cliente por ID.
   * @param {number} id - ID do cliente.
   * @returns {Promise<CustomerContent | Response>} - Dados completos do cliente.
   */
  static async findById(id: number): Promise<CustomerContent | Response> {
    return await http.get(`/customers/${id}`);
  }

  /**
   * Cria um novo cliente.
   * @param {CustomerInput} body - Dados do cliente a ser criado.
   * @returns {Promise<CustomerContent | Response>} - Cliente criado ou resposta de erro.
   */
  static async create(
    body: CustomerInput
  ): Promise<CustomerContent | Response> {
    return await http.post(body, '/customers');
  }

  /**
   * Edita um cliente existente.
   * @param {number} id - ID do cliente a ser atualizado.
   * @param {CustomerInput} body - Dados do cliente para atualização.
   * @returns {Promise<CustomerContent | Response>} - Cliente atualizado ou resposta de erro.
   */
  static async edit(
    id: number,
    body: CustomerInput
  ): Promise<CustomerContent | Response> {
    return await http.put(body, `/customers/${id}`);
  }

  /**
   * Importa clientes apenas para pré-visualização.
   * @param {FormData} file - Arquivo para importar clientes.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async importPreview(file: FormData): Promise<Response> {
    return await http.postFile(file, '/customers/import/preview');
  }

  /**
   * Processa a importação de clientes.
   * @param {FormData} body - Arquivo com os dados dos clientes para processar.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async importProcess(body: FormData): Promise<Response> {
    return await http.postFile(body, '/customers/import/process');
  }
}

export default CustomersService;
